import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import ResponsiveDialogActions from 'app/shared-components/layout/dialog/ResponsiveDialogActions';
import { sentryMask } from 'sentryReplayPrivacy';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

interface SpectrumConnectionModalProps {
  open: boolean;
  apiKey: string;
  endpointUrl: string;
  onClose: () => void;
}

function SpectrumConnectionModal({ open, apiKey, endpointUrl, onClose }: SpectrumConnectionModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar(t('erpConnection.copiedToClipboard', '{{label}} copied to clipboard', { label }), {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar(t('erpConnection.copyFailed', 'Failed to copy to clipboard'), { variant: 'error' });
    }
  };

  const handleClose = () => {
    setAcknowledged(false);
    onClose();
  };

  return (
    <ResponsiveDialog
      {...sentryMask}
      open={open}
      onClose={() => {
        if (acknowledged) handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{t('erpConnection.spectrumSetupTitle', 'Spectrum Connection Setup')}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t(
            'erpConnection.spectrumKeyWarning',
            'Save your API key now. For security, it will not be shown again. If you lose it, you will need to disconnect and reconnect.'
          )}
        </Alert>

        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          {t('erpConnection.endpointUrl', 'Endpoint URL')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TextField
            value={endpointUrl}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              },
            }}
          />
          <IconButton
            onClick={() => handleCopy(endpointUrl, 'Endpoint URL')}
            aria-label={t('erpConnection.copyEndpointUrl', 'Copy endpoint URL')}
          >
            <ContentCopyIcon />
          </IconButton>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          {t('erpConnection.apiKey', 'API Key')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <TextField
            value={apiKey}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              },
            }}
          />
          <IconButton
            onClick={() => handleCopy(apiKey, 'API Key')}
            aria-label={t('erpConnection.copyApiKey', 'Copy API key')}
          >
            <ContentCopyIcon />
          </IconButton>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.primary.main }}>
          {t('erpConnection.setupSteps', 'Setup Steps for AppXchange')}
        </Typography>
        <List dense sx={{ pl: 2 }}>
          <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', pl: 0 }}>
            <ListItemText
              primary={t(
                'erpConnection.stepAppxchangeInvite',
                'Contact your Speedchain representative to provision your Trimble AppXchange Customer Deployment, then accept the invitation when received'
              )}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', pl: 0 }}>
            <ListItemText primary={t('erpConnection.step1', 'Log into your Trimble AppXchange workspace')} />
          </ListItem>
          <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', pl: 0 }}>
            <ListItemText
              primary={t('erpConnection.step2', 'Open the HTTP connector and create a new connection')}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', pl: 0 }}>
            <ListItemText
              primary={t('erpConnection.step3', 'Paste the Endpoint URL above as the Base URL')}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', pl: 0 }}>
            <ListItemText
              primary={t(
                'erpConnection.step4',
                'Set the API Key Header to "X-Api-Key" and paste the API Key above'
              )}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', pl: 0 }}>
            <ListItemText
              primary={t(
                'erpConnection.step5',
                'Set the Test Endpoint to "/erp/v1" with expected status code 200'
              )}
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', pl: 0 }}>
            <ListItemText
              primary={t('erpConnection.step6', 'Test the connection — it should return success')}
            />
          </ListItem>
        </List>

        <FormControlLabel
          sx={{ mt: 3 }}
          control={<Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
          label={
            <Typography variant="body2">
              {t('erpConnection.acknowledgeKey', 'I have saved the API key in a secure location')}
            </Typography>
          }
        />
      </DialogContent>
      <ResponsiveDialogActions>
        <Button onClick={handleClose} variant="contained" disabled={!acknowledged}>
          {t('done', 'Done')}
        </Button>
      </ResponsiveDialogActions>
    </ResponsiveDialog>
  );
}

export default SpectrumConnectionModal;
